import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  subscribeToDiscipline, subscribeToEvent,
  savePools, resetPools, updateDiscipline, generatePoolBouts, deleteDisciplineBouts,
  subscribeToEventBouts, assignPoolJudges, assignPoolRef, assignPoolRing, releasePoolSeat,
  generateEliminationBouts, createNextRoundBout, resetElimination,
  updateEventFighterAffiliations, updateEventFighterWaivers
} from "../firebase/services";
import { PHASES, ROUND_OPTIONS, DEFAULT_DISCIPLINE_VISIBILITY } from "../utils/scoring";
import PoolStandings from "../components/PoolStandings";
import { EliminationSetup, BracketView, BracketTree } from "../components/EliminationBracket";
import TouchStatsSummary from "../components/TouchStatsSummary";
import InlineTextCell from "../components/InlineTextCell";
import { announceStatus } from "../utils/announceStatus";
import CodeQR from "../components/CodeQR";
import { calculateCombinedStandings, snakeSeedIntoPools } from "../utils/standings";
import { withoutPoolRef } from "../utils/poolAssignments";
import logo from "../assets/logo.png";

export default function DisciplineDetail() {
  const { eventId, disciplineId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [disc, setDisc] = useState(null);
  const [event, setEvent] = useState(null);
  const [bouts, setBouts] = useState([]);

  // Pool setup
  const [poolCount, setPoolCount] = useState(2);
  const [editingPools, setEditingPools] = useState(false);
  const [draftPools, setDraftPools] = useState([]);
  const [settingUpRound2, setSettingUpRound2] = useState(false);
  const [dragFighter, setDragFighter] = useState(null); // { poolIdx, fighter }
  const [dragOverPoolIdx, setDragOverPoolIdx] = useState(null);
  const [moveMenu, setMoveMenu] = useState(null);
  const [generatingBouts, setGeneratingBouts] = useState(false);
  const [showElimSetup, setShowElimSetup] = useState(false);

  // Tabs
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "setup";
  const setActiveTab = (tab) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("tab", tab);
    return next;
  }, { replace: true });

  // Pool collapse state — explicit user overrides, keyed by pool name. Absent
  // entries fall back to a computed default (round 1 pools auto-collapse once
  // round 2 pools exist, so round 2 is easy to find without scrolling).
  const [poolOpenOverride, setPoolOpenOverride] = useState({});
  const togglePool = (name, currentlyOpen) => {
    setPoolOpenOverride(prev => ({ ...prev, [name]: !currentlyOpen }));
  };

  // POOLS & BOUTS tab — pool cards scroll horizontally in one row on desktop;
  // arrow buttons drive it directly since a plain mouse wheel doesn't scroll
  // horizontal-only overflow regions (only trackpads/shift+wheel do).
  const poolsScrollRef = useRef(null);
  const settingUpdateVersions = useRef({});
  const scrollPools = (dir) => {
    poolsScrollRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  useEffect(() => {
    const unsub1 = subscribeToDiscipline(eventId, disciplineId, setDisc);
    const unsub2 = subscribeToEventBouts(eventId, (allBouts) => {
      setBouts(allBouts.filter(b => b.disciplineId === disciplineId));
    });
    const unsub3 = subscribeToEvent(eventId, setEvent);
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [eventId, disciplineId]);

  // ─── Fighters ───
  // Roster membership (add/remove, and which disciplines a fighter is in) is
  // edited from EventDashboard's FIGHTERS tab now — this page only edits
  // per-discipline check-in/waiver status and affiliation for whoever's already
  // registered here.
  const handleUpdateAffiliation = async (name, affiliation) => {
    const updated = { ...(event?.fighterAffiliations || {}), [name]: affiliation };
    try { await updateEventFighterAffiliations(eventId, updated); }
    catch (err) { console.error(err); }
  };

  const handleToggleCheckin = async (name) => {
    const current = (disc.fighterCheckins || {})[name] || false;
    const updated = { ...(disc.fighterCheckins || {}), [name]: !current };
    const data = { fighterCheckins: updated };
    if (!current && (disc.fighterDropped || {})[name]) {
      data.fighterDropped = { ...disc.fighterDropped, [name]: false };
    }
    try { await updateDiscipline(eventId, disciplineId, data); }
    catch (err) { console.error(err); }
  };

  const handleToggleWaiver = async (name) => {
    const current = (event?.fighterWaivers || {})[name] || false;
    const updated = { ...(event?.fighterWaivers || {}), [name]: !current };
    try { await updateEventFighterWaivers(eventId, updated); }
    catch (err) { console.error(err); }
  };

  // ─── Pools ───
  const autoDivide = async () => {
    const allFighters = disc.fighters || [];
    const checkins = disc.fighterCheckins || {};
    const notCheckedIn = allFighters.filter(f => !checkins[f]);

    if (notCheckedIn.length > 0) {
      const proceed = window.confirm(
        `The following fighters have not been checked in and will be dropped from the event:\n\n${notCheckedIn.join("\n")}\n\nProceed?`
      );
      if (!proceed) return;
      const updatedDropped = { ...(disc.fighterDropped || {}) };
      notCheckedIn.forEach(f => { updatedDropped[f] = true; });
      try { await updateDiscipline(eventId, disciplineId, { fighterDropped: updatedDropped }); }
      catch (err) { console.error(err); }
    }

    const fighters = allFighters.filter(f => checkins[f]);
    for (let i = fighters.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [fighters[i], fighters[j]] = [fighters[j], fighters[i]];
    }
    const pools = [];
    const count = Math.max(1, Math.min(poolCount, fighters.length));
    for (let i = 0; i < count; i++) {
      pools.push({ name: `Pool ${String.fromCharCode(65 + i)}`, fighters: [], ref: "" });
    }
    fighters.forEach((f, i) => { pools[i % count].fighters.push(f); });
    setDraftPools(pools);
    setEditingPools(true);
  };

  const moveFighter = (fromPool, toPool, fighter) => {
    setDraftPools(draftPools.map((p, i) => {
      if (i === fromPool) return { ...p, fighters: p.fighters.filter(f => f !== fighter) };
      if (i === toPool) return { ...p, fighters: [...p.fighters, fighter] };
      return p;
    }));
  };

  const handleSavePools = async () => {
    // Check for refs assigned to multiple pools
    const refCounts = {};
    draftPools.forEach(p => {
      if (p.ref) refCounts[p.ref] = (refCounts[p.ref] || 0) + 1;
    });
    const multiRefs = Object.entries(refCounts).filter(([_, count]) => count > 1);
    if (multiRefs.length > 0) {
      const names = multiRefs.map(([name, count]) => `${name} (${count} pools)`).join(", ");
      if (!window.confirm(`Warning: ${names} assigned to multiple pools. Make sure these pools aren't running at the same time.\n\nContinue?`)) {
        return;
      }
    }
    try { await savePools(eventId, disciplineId, draftPools, ""); setEditingPools(false); }
    catch (err) { console.error(err); alert("Failed to save"); }
  };

  const handleUpdateSettings = async (field, value) => {
    const previousValue = disc[field];
    const version = (settingUpdateVersions.current[field] || 0) + 1;
    settingUpdateVersions.current[field] = version;
    setDisc((current) => current ? { ...current, [field]: value } : current);
    try {
      await updateDiscipline(eventId, disciplineId, { [field]: value });
    } catch (err) {
      console.error(err);
      if (settingUpdateVersions.current[field] === version) {
        setDisc((current) => {
          if (!current || current[field] !== value) return current;
          const restored = { ...current };
          if (previousValue === undefined) delete restored[field];
          else restored[field] = previousValue;
          return restored;
        });
        alert("Failed to update setting");
      }
    }
  };

  const handleGenerateBouts = async () => {
    if (!window.confirm("Generate round-robin bouts for all pools?")) return;
    setGeneratingBouts(true);
    try { await generatePoolBouts(eventId, disciplineId, disc, event?.fighterUids || {}); }
    catch (err) { console.error(err); alert("Failed"); }
    setGeneratingBouts(false);
  };

  const handleResetPools = async () => {
    const hasActiveBouts = bouts.some(b => b.status !== "waiting");
    const msg = hasActiveBouts
      ? "WARNING: Some bouts have already been scored. Resetting pools will delete ALL bouts for this discipline, including scored ones. This cannot be undone.\n\nContinue?"
      : "Reset pools? This will also delete any generated bouts. You can then re-divide fighters into new pools.";
    if (!window.confirm(msg)) return;
    try {
      await deleteDisciplineBouts(eventId, disciplineId);
      await resetPools(eventId, disciplineId);
    } catch (err) { console.error(err); alert("Failed to reset"); }
  };

  const handleRegenerateBouts = async () => {
    const hasActiveBouts = bouts.some(b => b.status !== "waiting");
    const msg = hasActiveBouts
      ? "WARNING: Some bouts have already been scored. Regenerating will delete ALL existing bouts (including scored ones) and create fresh ones. This cannot be undone.\n\nContinue?"
      : "Delete existing bouts and regenerate fresh round-robin bouts for all pools?";
    if (!window.confirm(msg)) return;
    setGeneratingBouts(true);
    try {
      await deleteDisciplineBouts(eventId, disciplineId);
      await generatePoolBouts(eventId, disciplineId, disc, event?.fighterUids || {});
    } catch (err) { console.error(err); alert("Failed"); }
    setGeneratingBouts(false);
  };

  const handleSetupRound2Pools = () => {
    const round1Pools = (disc.pools || []).filter(p => (p.round || 1) === 1);
    const ranked = standings.map(s => s.name);
    const seededGroups = snakeSeedIntoPools(ranked, round1Pools.length || 1);
    setDraftPools(round1Pools.map((p, i) => ({
      name: `${p.name} R2`,
      fighters: seededGroups[i] || [],
      ref: "",
      round: 2,
      basePool: p.name,
    })));
    setSettingUpRound2(true);
    setEditingPools(true);
  };

  const handleSaveRound2Pools = async () => {
    const refCounts = {};
    draftPools.forEach(p => {
      if (p.ref) refCounts[p.ref] = (refCounts[p.ref] || 0) + 1;
    });
    const multiRefs = Object.entries(refCounts).filter(([_, count]) => count > 1);
    if (multiRefs.length > 0) {
      const names = multiRefs.map(([name, count]) => `${name} (${count} pools)`).join(", ");
      if (!window.confirm(`Warning: ${names} assigned to multiple pools. Make sure these pools aren't running at the same time.\n\nContinue?`)) {
        return;
      }
    }
    try {
      const round1Pools = (disc.pools || []).filter(p => (p.round || 1) === 1);
      await savePools(eventId, disciplineId, [...round1Pools, ...draftPools], "");
      setEditingPools(false);
      setSettingUpRound2(false);
    } catch (err) { console.error(err); alert("Failed to save"); }
  };

  const handleGenerateRound2Bouts = async () => {
    if (!window.confirm("Generate round-robin bouts for round 2 pools?")) return;
    setGeneratingBouts(true);
    try {
      const round2Pools = (disc.pools || []).filter(p => (p.round || 1) === 2);
      await generatePoolBouts(eventId, disciplineId, { ...disc, pools: round2Pools }, event?.fighterUids || {});
    } catch (err) { console.error(err); alert("Failed"); }
    setGeneratingBouts(false);
  };

  const handleConfirmElimination = async (bracketSize, matchups, stageConfig) => {
    try {
      await generateEliminationBouts(eventId, disciplineId, disc.name, matchups, stageConfig, bracketSize);
      setShowElimSetup(false);
    } catch (err) { console.error(err); alert("Failed to create elimination bouts"); }
  };

  // Auto-advance winners when bouts are confirmed
  const advancedRef = useRef(new Set());
  useEffect(() => {
    if (!disc?.elimination || !bouts.length) return;
    const stageConfig = disc.elimination.stageConfig || [];
    const confirmedElimBouts = bouts.filter(b => b.eliminationRound != null && b.status === "confirmed" && b.confirmedResult?.winner);
    
    confirmedElimBouts.forEach(async (bout) => {
      try {
        const currentRound = bout.eliminationRound;
        const nextRound = currentRound + 1;
        const nextMatchIndex = Math.floor(bout.eliminationMatch / 2);
        const nextStage = stageConfig[nextRound];
        if (!nextStage) return;

        // Deduplicate: only process once per next-round match
        const advanceKey = `${nextRound}-${nextMatchIndex}`;
        if (advancedRef.current.has(advanceKey)) return;

        // Check if next-round bout already exists
        const existingNext = bouts.find(b => b.eliminationRound === nextRound && b.eliminationMatch === nextMatchIndex);
        if (existingNext) {
          advancedRef.current.add(advanceKey);
          return;
        }

        // Check if sibling bout is also done
        const siblingMatch = bout.eliminationMatch % 2 === 0 ? bout.eliminationMatch + 1 : bout.eliminationMatch - 1;
        const siblingBout = bouts.find(b => b.eliminationRound === currentRound && b.eliminationMatch === siblingMatch);
        if (!siblingBout?.confirmedResult?.winner) return;

        // Mark as processing BEFORE the async call
        advancedRef.current.add(advanceKey);

        const isEvenMatch = bout.eliminationMatch % 2 === 0;
        const fighter1 = isEvenMatch ? bout.confirmedResult.winner : siblingBout.confirmedResult.winner;
        const fighter2 = isEvenMatch ? siblingBout.confirmedResult.winner : bout.confirmedResult.winner;

        await createNextRoundBout(eventId, disciplineId, disc.name, nextRound, nextMatchIndex, fighter1, fighter2, nextStage.roundsPerBout, nextStage.phase, event?.fighterUids || {});
      } catch (err) {
        advancedRef.current.delete(`${Math.floor(bout.eliminationRound + 1)}-${Math.floor(bout.eliminationMatch / 2)}`);
        console.error("Auto-advance error:", err);
      }
    });
  }, [bouts, disc?.elimination]);

  // Calculate standings for elimination setup (must be before early return)
  const poolBoutsForStandings = bouts.filter(b => b.eliminationRound == null);
  const standings = useMemo(() => {
    const pools = disc?.pools || [];
    if (!pools.length || !poolBoutsForStandings.length) return [];
    if (!poolBoutsForStandings.some(b => b.status === "confirmed")) return [];
    return calculateCombinedStandings(pools, poolBoutsForStandings);
  }, [disc?.pools, poolBoutsForStandings]);

  if (!disc || !event) return <div className="flex items-center justify-center min-h-screen text-gray-600">Loading...</div>;

  if (event.createdBy && event.createdBy !== user?.uid) {
    return <Navigate to={`/public/${eventId}/discipline/${disciplineId}`} replace />;
  }

  const fighters = disc.fighters || [];
  const checkedInFighters = fighters.filter(f => (disc.fighterCheckins || {})[f]);
  const officials = event?.officials || [];
  const pools = disc.pools || [];
  const elimination = disc.elimination || null;
  const elimBouts = bouts.filter(b => b.eliminationRound != null);
  const poolBouts = bouts.filter(b => b.eliminationRound == null);
  const poolRoundsConfigured = disc.poolRounds || 1;
  const round1Pools = pools.filter(p => (p.round || 1) === 1);
  const round2Pools = pools.filter(p => (p.round || 1) === 2);
  const round1PoolBouts = poolBouts.filter(b => round1Pools.some(p => p.name === b.poolName));
  const round2PoolBouts = poolBouts.filter(b => round2Pools.some(p => p.name === b.poolName));
  const round1Done = round1PoolBouts.length > 0 && round1PoolBouts.every(b => b.status === "confirmed");
  const round2Done = round2Pools.length > 0 && round2PoolBouts.length > 0 && round2PoolBouts.every(b => b.status === "confirmed");
  const readyForRound2Setup = poolRoundsConfigured === 2 && round1Done && round2Pools.length === 0;
  const readyForRound2Bouts = round2Pools.length > 0 && round2PoolBouts.length === 0;
  const allPoolBoutsDone = poolRoundsConfigured === 2
    ? (round1Done && round2Done)
    : (poolBouts.length > 0 && poolBouts.every(b => b.status === "confirmed"));
  const elimReady = allPoolBoutsDone && !elimination && standings.length > 0;

  const TABS = [
    { key: "setup", label: "SETUP" },
    { key: "fighters", label: `FIGHTERS${fighters.length ? ` (${fighters.length})` : ""}` },
    { key: "pools", label: `POOLS & BOUTS${pools.length ? ` (${pools.length})` : ""}` },
    { key: "standings", label: "STANDINGS" },
    { key: "elimination", label: "ELIMINATION", flag: elimReady },
    { key: "bracket", label: "BRACKET" },
    { key: "summary", label: "SUMMARY" },
  ];

  return (
    <div className="min-h-screen p-5 lg:max-w-[1600px] lg:mx-auto">
      <Link to={`/event/${eventId}`} className="text-gray-600 text-sm no-underline hover:text-gray-400">← Back to {event?.name || "Event"}</Link>

      <div className="text-center my-4">
        <img src={logo} alt="Joy of HEMA" className="w-80 mx-auto" />
        <div className="flex items-center gap-3 max-w-[280px] mx-auto mt-2">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-400 text-sm font-display font-bold tracking-[0.25em] whitespace-nowrap">
            STUDY &bull; FENCE &bull; GROW
          </span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 mb-4">
        <div>
          <h1 className="font-display text-xl font-bold">{disc.name}</h1>
          {event?.name && <div className="text-gray-600 text-xs mt-0.5">{event.name}</div>}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-5 border-b border-dark-500 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} aria-pressed={activeTab === t.key}
            className="relative px-4 py-2.5 font-display text-xs font-bold tracking-widest whitespace-nowrap border-b-2 bg-transparent cursor-pointer"
            style={{ color: activeTab === t.key ? "#fff" : "#bbb", borderColor: activeTab === t.key ? "#81C784" : "transparent" }}>
            {t.label}
            {t.flag && <span className="absolute top-1.5 right-1 w-1.5 h-1.5 rounded-full" style={{ background: "#FF9800" }} />}
          </button>
        ))}
      </div>

      {activeTab === "setup" && (
      <div>

      {/* Settings */}
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-5">
        <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">SETTINGS</div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1.5">SCORING SCALE</label>
            <div className="flex gap-1.5">
              {PHASES.map(p => (
                <button key={p.key} onClick={() => handleUpdateSettings("phase", p.key)} aria-pressed={disc.phase === p.key}
                  className="flex-1 p-2 rounded-lg text-center text-xs font-display font-bold border cursor-pointer"
                  style={{ background: disc.phase === p.key ? "#1a2a1a" : "#111", borderColor: disc.phase === p.key ? "#81C784" : "#666", color: disc.phase === p.key ? "#fff" : "#bbb" }}>
                  {p.label}{disc.phase === p.key ? " ✓" : ""}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1">
            <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1.5">ROUNDS / BOUT</label>
            <div className="flex gap-1.5">
              {ROUND_OPTIONS.map(n => (
                <button key={n} onClick={() => handleUpdateSettings("roundsPerBout", n)} aria-pressed={disc.roundsPerBout === n} aria-label={`${n} rounds per bout`}
                  className="flex-1 p-2 rounded-lg text-center font-display text-base font-extrabold border cursor-pointer"
                  style={{ background: disc.roundsPerBout === n ? "#1a2a1a" : "#111", borderColor: disc.roundsPerBout === n ? "#81C784" : "#666", color: disc.roundsPerBout === n ? "#fff" : "#bbb" }}>
                  {n}{disc.roundsPerBout === n ? " ✓" : ""}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Pool rounds */}
        <div className="mt-4">
          <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1.5">POOL ROUNDS</label>
          <div className="flex gap-1.5">
            {[{ n: 1, label: "ONE ROUND" }, { n: 2, label: "TWO ROUNDS" }].map(({ n, label }) => (
              <button key={n} onClick={() => handleUpdateSettings("poolRounds", n)} aria-pressed={(disc.poolRounds || 1) === n}
                className="flex-1 p-2 rounded-lg text-center text-xs font-display font-bold border cursor-pointer"
                style={{ background: (disc.poolRounds || 1) === n ? "#1a2a1a" : "#111", borderColor: (disc.poolRounds || 1) === n ? "#81C784" : "#666", color: (disc.poolRounds || 1) === n ? "#fff" : "#bbb" }}>
                {label}{(disc.poolRounds || 1) === n ? " ✓" : ""}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-600 mt-1.5">
            Two rounds re-seeds fighters into new pools (based on round 1 standings) for a second pool round before elimination. Final standings sum both rounds.
          </p>
        </div>

        {/* Rings */}
        <div className="mt-4">
          <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1.5">NUMBER OF RINGS</label>
          <div className="flex gap-1.5">
            {[1, 2, 3, 4, 5, 6].map(n => (
              <button key={n} onClick={() => handleUpdateSettings("numRings", n)} aria-pressed={disc.numRings === n} aria-label={`${n} rings`}
                className="min-w-9 h-9 px-1 rounded-lg font-display text-sm font-extrabold border cursor-pointer"
                style={{ background: disc.numRings === n ? "#1a2a1a" : "#111", borderColor: disc.numRings === n ? "#81C784" : "#666", color: disc.numRings === n ? "#fff" : "#bbb" }}>
                {n}{disc.numRings === n ? " ✓" : ""}
              </button>
            ))}
          </div>
        </div>

        {/* Visibility toggles */}
        <div className="mt-4">
          <label className="text-xs font-bold text-gray-500 tracking-wider block mb-2">PUBLIC VISIBILITY</label>
          <p className="text-gray-600 text-[10px] mb-2">Control what others see for this discipline.</p>
          {[
            { key: "pools", label: "Pools & Bouts", desc: "Show pool assignments and bout list" },
            { key: "scores", label: "Pool Bout Scores", desc: "Show confirmed pool results" },
            { key: "standings", label: "Pool Standings", desc: "Show rankings, P/I, P/R" },
            { key: "elimBracket", label: "Elimination Bracket", desc: "Show bracket matchups" },
            { key: "elimScores", label: "Elimination Scores", desc: "Show elimination bout results" },
            { key: "judgeBreakdown", label: "Judge Breakdown", desc: "Per-judge scoring details" },
          ].map(opt => {
            const vis = disc.visibility || DEFAULT_DISCIPLINE_VISIBILITY;
            const isOn = vis[opt.key] ?? DEFAULT_DISCIPLINE_VISIBILITY[opt.key];
            return (
              <button key={opt.key} role="switch" aria-checked={isOn} aria-label={`${opt.label} public visibility`} onClick={() => {
                const updated = { ...(disc.visibility || DEFAULT_DISCIPLINE_VISIBILITY), [opt.key]: !isOn };
                handleUpdateSettings("visibility", updated);
              }}
                className="w-full flex items-center justify-between p-2 mb-1 rounded-lg border cursor-pointer text-left"
                style={{ background: isOn ? "#1a2a1a" : "#111", borderColor: isOn ? "#81C784" : "#666" }}>
                <div className="text-left">
                  <div className="text-xs font-semibold" style={{ color: "#fff" }}>{opt.label}</div>
                  <div className="text-[10px]" style={{ color: "#bbb" }}>{opt.desc}</div>
                </div>
                <div className="rounded-full flex items-center px-0.5 transition-colors"
                  style={{ background: isOn ? "#1B5E20" : "#555", border: "1px solid #aaa", justifyContent: isOn ? "flex-end" : "flex-start", width: 36, height: 18 }}>
                  <div className="rounded-full bg-white" style={{ width: 14, height: 14 }} />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      </div>
      )}

      {activeTab === "fighters" && (
      <div>

      {/* Fighters Roster */}
      <div className="mb-5">
        <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">FIGHTERS ({fighters.length})</div>
        <p className="text-gray-600 text-xs mb-3">
          Add, remove, or register fighters for this discipline from the event's{" "}
          <Link to={`/event/${eventId}?tab=fighters`} className="underline hover:text-gray-400">FIGHTERS tab</Link>.
          Check-in is per discipline; a waiver is signed once for the whole tournament and
          applies everywhere the fighter is entered.
        </p>
        {fighters.length > 0 && (
          <div className="overflow-x-auto border border-dark-500 rounded-lg">
            <table className="w-full text-sm border-collapse">
              <caption className="sr-only">Discipline fighter check-in</caption>
              <thead>
                <tr className="bg-dark-700">
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">NAME</th>
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">AFFILIATION</th>
                  <th scope="col" className="text-center p-2.5 text-xs font-bold text-gray-500 tracking-wider">CHECKED IN</th>
                  <th scope="col" className="text-center p-2.5 text-xs font-bold text-gray-500 tracking-wider">WAIVER</th>
                </tr>
              </thead>
              <tbody>
                {fighters.sort().map(name => {
                  const checkedIn = (disc.fighterCheckins || {})[name] || false;
                  const dropped = !checkedIn && ((disc.fighterDropped || {})[name] || false);
                  const waiverSigned = (event?.fighterWaivers || {})[name] || false;
                  return (
                  <tr key={name} className="border-t border-dark-500">
                    <th scope="row" className="p-2.5 text-left font-normal text-gray-300 whitespace-nowrap">{name}</th>
                    <td className="p-2.5">
                      <InlineTextCell label={`Affiliation for ${name}`} value={(event?.fighterAffiliations || {})[name] || ""}
                        onSave={(val) => handleUpdateAffiliation(name, val)} />
                    </td>
                    <td className="p-2.5 text-center">
                      <button onClick={() => handleToggleCheckin(name)}
                        title={dropped ? "Dropped — no show. Click to check in." : undefined}
                        className={`px-2.5 py-1 rounded-md text-xs font-display font-bold tracking-wide border-none cursor-pointer ${checkedIn ? "bg-green-action-light text-white" : dropped ? "bg-red-900 text-red-200 hover:text-red-100" : "bg-dark-600 text-gray-500 hover:text-gray-300"}`}>
                        {checkedIn ? "✓ IN" : dropped ? "NO SHOW" : "CHECK IN"}
                      </button>
                    </td>
                    <td className="p-2.5 text-center">
                      <button onClick={() => handleToggleWaiver(name)}
                        className={`px-2.5 py-1 rounded-md text-xs font-display font-bold tracking-wide border-none cursor-pointer ${waiverSigned ? "bg-green-action-light text-white" : "bg-dark-600 text-gray-500 hover:text-gray-300"}`}>
                        {waiverSigned ? "✓ SIGNED" : "UNSIGNED"}
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      </div>
      )}

      {activeTab === "pools" && (
      <div>

        {fighters.length < 2 && (
          <p className="text-gray-600 text-sm">Add at least 2 fighters to set up pools.</p>
        )}

        {fighters.length >= 2 && checkedInFighters.length < 2 && (
          <p className="text-gray-600 text-sm">At least 2 checked-in fighters are needed to set up pools.</p>
        )}

        {checkedInFighters.length >= 2 && !editingPools && pools.length === 0 && (
          <div className="bg-dark-700 border border-dark-500 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <label className="text-xs font-bold text-gray-500">NUMBER OF POOLS</label>
              <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5, 6].filter(n => n <= Math.floor(checkedInFighters.length / 2)).map(n => (
                  <button key={n} onClick={() => setPoolCount(n)} aria-pressed={poolCount === n} aria-label={`${n} pools`}
                    className="min-w-9 h-9 px-1 rounded-lg font-display text-sm font-extrabold border cursor-pointer"
                    style={{ background: poolCount === n ? "#1a2a1a" : "#111", borderColor: poolCount === n ? "#81C784" : "#666", color: poolCount === n ? "#fff" : "#bbb" }}>
                    {n}{poolCount === n ? " ✓" : ""}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={autoDivide}
              className="w-full p-3 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide border-none cursor-pointer">
              Auto-Divide {checkedInFighters.length} Fighters into {poolCount} Pools
            </button>
          </div>
        )}

        {!editingPools && pools.length > 0 && (
          <>
            <div className="flex gap-2 mb-2">
              <button onClick={() => { setDraftPools(pools.map(p => ({ ...p }))); setSettingUpRound2(false); setEditingPools(true); }}
                className="flex-1 p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-gray-400 text-sm font-bold cursor-pointer">
                Edit Pools
              </button>
              {bouts.length === 0 ? (
                <button onClick={handleGenerateBouts} disabled={generatingBouts}
                  className="flex-1 p-2.5 bg-green-action-light text-white rounded-lg text-sm font-display font-bold tracking-wide disabled:opacity-40 border-none cursor-pointer">
                  {generatingBouts ? "Generating..." : "Generate Bouts"}
                </button>
              ) : (
                <button onClick={handleRegenerateBouts} disabled={generatingBouts}
                  className="flex-1 p-2.5 bg-dark-800 border border-yellow-700 rounded-lg text-yellow-500 text-sm font-bold cursor-pointer disabled:opacity-40">
                  {generatingBouts ? "Regenerating..." : "Regenerate Bouts"}
                </button>
              )}
            </div>
            <button onClick={handleResetPools}
              className="w-full p-2 mb-4 bg-transparent border border-dark-500 rounded-lg text-gray-600 text-xs cursor-pointer hover:text-red-400 hover:border-red-900">
              Reset Pools & Start Over
            </button>

            {readyForRound2Setup && (
              <div className="bg-dark-700 border border-yellow-700 rounded-xl p-4 mb-4">
                <div className="font-display text-sm font-bold text-white mb-1">Round 1 pools complete</div>
                <p className="text-gray-500 text-xs mb-3">Re-seed fighters into new pools for round 2, based on round 1 standings.</p>
                <button onClick={handleSetupRound2Pools}
                  className="w-full p-3 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide border-none cursor-pointer">
                  Set Up Round 2 Pools
                </button>
              </div>
            )}

            {readyForRound2Bouts && (
              <div className="bg-dark-700 border border-yellow-700 rounded-xl p-4 mb-4">
                <div className="font-display text-sm font-bold text-white mb-1">Round 2 pools saved</div>
                <p className="text-gray-500 text-xs mb-3">Generate round-robin bouts for the new round 2 pools.</p>
                <button onClick={handleGenerateRound2Bouts} disabled={generatingBouts}
                  className="w-full p-3 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide border-none cursor-pointer disabled:opacity-40">
                  {generatingBouts ? "Generating..." : "Generate Round 2 Bouts"}
                </button>
              </div>
            )}

            <div className="relative">
              {pools.length > 3 && (
                <div className="hidden lg:flex items-center justify-end gap-2 mb-2">
                  <button onClick={() => scrollPools(-1)} title="Scroll left"
                    className="w-8 h-8 rounded-full bg-dark-700 border border-dark-500 text-gray-400 hover:text-white hover:border-gray-400 cursor-pointer">◀</button>
                  <button onClick={() => scrollPools(1)} title="Scroll right"
                    className="w-8 h-8 rounded-full bg-dark-700 border border-dark-500 text-gray-400 hover:text-white hover:border-gray-400 cursor-pointer">▶</button>
                </div>
              )}
              <div ref={poolsScrollRef} className="lg:flex lg:flex-nowrap lg:gap-4 lg:items-start lg:overflow-x-auto lg:pb-2 lg:scroll-smooth">
                {(() => {
                  // Group each round 2 pool underneath the round 1 pool it was seeded
                  // from (via basePool), so "Pool A" and "Pool A R2" stack together
                  // instead of being scattered across the row in disc.pools order.
                  const round1List = pools.filter(p => (p.round || 1) === 1);
                  const round2List = pools.filter(p => (p.round || 1) === 2);
                  const groups = round1List.map(p1 => ({
                    key: p1.name,
                    cards: [p1, ...round2List.filter(p2 => p2.basePool === p1.name)],
                  }));
                  round2List.filter(p2 => !round1List.some(p1 => p1.name === p2.basePool))
                    .forEach(p2 => groups.push({ key: p2.name, cards: [p2] }));

                  return groups.map(group => (
                    <div key={group.key} className="flex flex-col gap-2 mb-5 lg:mb-0 lg:flex-1" style={{ minWidth: 280, maxWidth: 420 }}>
                      {group.cards.map(pool => {
                        const thisBouts = poolBouts.filter(b => b.poolName === pool.name).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                        const completed = thisBouts.filter(b => b.status === "confirmed").length;
                        const defaultOpen = !(round2Pools.length > 0 && (pool.round || 1) === 1);
                        const isOpen = poolOpenOverride[pool.name] ?? defaultOpen;
                        return (
                          <PoolCard
                            key={pool.name}
                            pool={pool}
                            allPools={pools}
                            poolBouts={thisBouts}
                            completed={completed}
                            officials={officials}
                            officialRoles={event?.officialRoles || {}}
                            numRings={disc.numRings || 0}
                            eventId={eventId}
                            disciplineId={disciplineId}
                            navigate={navigate}
                            isOpen={isOpen}
                            onToggle={() => togglePool(pool.name, isOpen)}
                          />
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            </div>
            {poolBouts.filter(b => !b.poolName).map(b => <BoutCard key={b.id} bout={b} eventId={eventId} disciplineId={disciplineId} navigate={navigate} />)}
          </>
        )}

        {/* Editing pools */}
        {editingPools && (
          <>
            <div className="flex flex-col gap-3 mb-3">
              {draftPools.map((pool, poolIdx) => (
                <div key={poolIdx}
                  onDragOver={(e) => { e.preventDefault(); if (dragOverPoolIdx !== poolIdx) setDragOverPoolIdx(poolIdx); }}
                  onDragLeave={() => setDragOverPoolIdx(prev => (prev === poolIdx ? null : prev))}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragFighter && dragFighter.poolIdx !== poolIdx) moveFighter(dragFighter.poolIdx, poolIdx, dragFighter.fighter);
                    setDragFighter(null);
                    setDragOverPoolIdx(null);
                  }}
                  className="bg-dark-700 border rounded-xl p-3 transition-colors"
                  style={{ borderColor: dragOverPoolIdx === poolIdx ? "#4CAF50" : undefined, borderWidth: dragOverPoolIdx === poolIdx ? 2 : 1 }}>
                  <div id={`draft-pool-${poolIdx}`} tabIndex={-1} className="font-display text-sm font-bold text-white mb-2">{pool.name} ({pool.fighters.length})</div>

                  {/* Fighters */}
                  <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
                    {pool.fighters.map(f => (
                      <div key={f} className="group relative">
                        <button
                          type="button"
                          draggable
                          aria-haspopup="menu"
                          aria-expanded={moveMenu === `${poolIdx}:${f}`}
                          aria-label={`${f} — drag or press to move to another pool`}
                          onDragStart={(e) => { setMoveMenu(null); setDragFighter({ poolIdx, fighter: f }); e.dataTransfer.effectAllowed = "move"; }}
                          onDragEnd={() => { setDragFighter(null); setDragOverPoolIdx(null); }}
                          onClick={() => setMoveMenu(moveMenu === `${poolIdx}:${f}` ? null : `${poolIdx}:${f}`)}
                          onKeyDown={(e) => { if (e.key === "Escape") setMoveMenu(null); }}
                          className="px-2.5 py-1 min-h-6 bg-dark-800 border border-dark-500 rounded-lg text-xs text-gray-300 cursor-grab active:cursor-grabbing hover:border-gray-400 hover:text-white"
                          style={{ opacity: dragFighter?.fighter === f && dragFighter?.poolIdx === poolIdx ? 0.4 : 1 }}>
                          {f}
                        </button>
                        {moveMenu === `${poolIdx}:${f}` && <div role="menu" aria-label={`Move ${f} to`}
                          className="absolute top-full left-0 mt-1 z-10 bg-dark-900 border border-dark-500 rounded-lg shadow-lg flex flex-col"
                          onKeyDown={(e) => { if (e.key === "Escape") { setMoveMenu(null); e.currentTarget.previousElementSibling?.focus(); } }}>
                          {draftPools.map((tp, ti) => {
                            if (ti === poolIdx) return null;
                            return (
                               <button key={ti} role="menuitem" onClick={() => { moveFighter(poolIdx, ti, f); setMoveMenu(null); requestAnimationFrame(() => document.getElementById(`draft-pool-${ti}`)?.focus()); }}
                                className="px-3 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-dark-700 bg-transparent border-none cursor-pointer text-left whitespace-nowrap">
                                → {tp.name}
                              </button>
                            );
                          })}
                        </div>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-gray-600 text-xs mb-3">
              {settingUpRound2
                ? "Round 2 pools, seeded from round 1 standings. Drag a name into another pool, or tap it to pick one."
                : "Drag a name into another pool, or tap it to pick one. Assign a ref from each pool's card once saved."}
            </p>
            <div className="flex gap-2">
              <button onClick={() => { setEditingPools(false); setSettingUpRound2(false); }}
                className="flex-1 p-2.5 bg-dark-800 border border-dark-500 rounded-lg text-gray-500 text-sm font-bold cursor-pointer">Cancel</button>
              <button onClick={settingUpRound2 ? handleSaveRound2Pools : handleSavePools}
                className="flex-1 p-2.5 bg-green-action-light text-white rounded-lg text-sm font-display font-bold tracking-wide border-none cursor-pointer">
                {settingUpRound2 ? "Save Round 2 Pools" : "Save Pools"}
              </button>
            </div>
          </>
        )}
      </div>
      )}

      {activeTab === "standings" && (
      <div>
        {/* Pool Standings */}
        {bouts.filter(b => b.status === "confirmed").length > 0 && pools.length > 0 ? (
          <div className="mb-5">
            <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">POOL STANDINGS</div>
            <PoolStandings pools={pools} bouts={bouts} showBracketCutoff={false} />
          </div>
        ) : (
          <p className="text-gray-600 text-sm">Standings appear once pool bouts are confirmed.</p>
        )}

        {/* Elimination — points to the Elimination tab */}
        {elimReady && (
          <div className="mb-5">
            <button onClick={() => setActiveTab("elimination")}
              className="w-full p-4 bg-gradient-to-br from-yellow-700 to-yellow-900 text-white font-display text-base font-bold rounded-xl tracking-wide border-none cursor-pointer">
              ⚔ Ready — Set Up Elimination Bracket
            </button>
          </div>
        )}
      </div>
      )}

      {activeTab === "elimination" && (
      <div>
        {!elimination && !showElimSetup && (
          <p className="text-gray-600 text-sm">
            {elimReady
              ? "Ready to set up the elimination bracket."
              : readyForRound2Setup
                ? "Round 1 pools complete — set up Round 2 pools from the Pools & Bouts tab before advancing to elimination."
                : readyForRound2Bouts
                  ? "Round 2 pools are saved — generate Round 2 bouts from the Pools & Bouts tab."
                  : "Complete pool bouts and check the Standings tab to advance to elimination."}
          </p>
        )}

        {/* Elimination Setup trigger */}
        {elimReady && !showElimSetup && (
          <div className="mb-5">
            <button onClick={() => setShowElimSetup(true)}
              className="w-full p-4 bg-gradient-to-br from-yellow-700 to-yellow-900 text-white font-display text-base font-bold rounded-xl tracking-wide border-none cursor-pointer">
              ⚔ Advance to Elimination Bracket
            </button>
          </div>
        )}

        {/* Elimination Setup */}
        {showElimSetup && (
          <EliminationSetup
            standings={standings}
            onConfirm={handleConfirmElimination}
            onCancel={() => setShowElimSetup(false)}
          />
        )}

        {/* Elimination Bracket View */}
        {elimination && elimBouts.length > 0 && (
          <div className="mb-5">
            <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">ELIMINATION BRACKET</div>
            <BracketView
              bracketSize={elimination.bracketSize}
              bouts={bouts}
              eventId={eventId}
              officials={officials}
              officialRoles={event?.officialRoles || {}}
              onBoutClick={(bout) => navigate(`/event/${eventId}/discipline/${disciplineId}/bout/${bout.id}`)}
              onUpdateBout={async (boutId, data) => {
                const { updateBout } = await import("../firebase/services");
                await updateBout(eventId, boutId, data);
              }}
            />
            <button onClick={async () => {
              const hasScored = elimBouts.some(b => b.status !== "waiting" && !b.confirmedResult?.isBye);
              const msg = hasScored
                ? "WARNING: Some elimination bouts have been scored. This will delete ALL elimination bouts and let you re-create the bracket. This cannot be undone.\n\nContinue?"
                : "Reset the elimination bracket? You can then re-create it with different settings.";
              if (!window.confirm(msg)) return;
              await resetElimination(eventId, disciplineId);
            }}
              className="w-full p-2 mt-1 bg-transparent border border-dark-500 rounded-lg text-gray-600 text-xs cursor-pointer hover:text-red-400 hover:border-red-900">
              Reset Elimination Bracket
            </button>
          </div>
        )}
      </div>
      )}

      {activeTab === "bracket" && (
      <div>
        {elimination ? (
          <BracketTree bracketSize={elimination.bracketSize} bouts={bouts} />
        ) : (
          <p className="text-gray-600 text-sm">The bracket view appears once the elimination bracket has been created — see the Elimination tab.</p>
        )}
      </div>
      )}

      {activeTab === "summary" && (
      <div>
        <TouchStatsSummary bouts={bouts} affiliations={event?.fighterAffiliations || {}} />
      </div>
      )}
    </div>
  );
}

// One labeled band inside a PoolCard. The hairline + consistent label style is what
// keeps the card's stack (roster / officials / access codes) from reading as one long
// undifferentiated column — every section in the card goes through this.
// The divider is an explicit #2a2a2a rather than border-dark-500: the dark palette's
// 700/800/500 steps (#141414/#111111/#222222) are too close to separate anything on
// a dark-700 card, which is what made the old flat stack run together in the first place.
function PoolSection({ label, action, children }) {
  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: "#2a2a2a" }}>
      <div className="flex items-center justify-between mb-2.5 min-h-5">
        <PoolSectionLabel>{label}</PoolSectionLabel>
        {action}
      </div>
      {children}
    </div>
  );
}

// Section headings sit directly above field labels (REF, RING, JUDGES), so the two
// have to look like different ranks: sections are brighter, larger and wider-tracked
// with a leading rule; fields stay small and dim. Same style everywhere so the
// standalone access card matches the bands inside the pool card.
function PoolSectionLabel({ children }) {
  return (
    <span className="flex items-center gap-2">
      <span aria-hidden="true" className="inline-block w-3 h-px" style={{ background: "#4a4a4a" }} />
      <span className="font-display text-[11px] font-bold text-gray-300 tracking-[0.18em]">{children}</span>
    </span>
  );
}

// A single typed-entry code, boxed. Sits a step darker than the card it's in so the
// code reads as a thing to be handed out rather than another line of card text.
function CodeBox({ label, code, accent }) {
  return (
    <div className="rounded-lg border px-2 py-2 text-center" style={{ background: "#0d0d0d", borderColor: "#2a2a2a" }}>
      <div className="font-display text-[10px] font-bold text-gray-600 tracking-widest">{label}</div>
      <div className="font-display text-2xl font-extrabold tracking-[0.1em] mt-0.5" style={{ color: accent }}>{code}</div>
    </div>
  );
}

function PoolCard({ pool, allPools, poolBouts, completed, officials, officialRoles, numRings, eventId, disciplineId, navigate, isOpen, onToggle }) {
  const [editingJudges, setEditingJudges] = useState(false);
  const [selectedJudges, setSelectedJudges] = useState([]);
  const [saving, setSaving] = useState(false);
  const [displayRef, setDisplayRef] = useState(pool.ref || "");

  const currentJudges = pool.judges || [];
  const claimedJudges = pool.claimedJudges || [];

  useEffect(() => {
    setDisplayRef(pool.ref || "");
  }, [pool.ref]);

  useEffect(() => {
    if (editingJudges && displayRef) {
      setSelectedJudges(current => withoutPoolRef(current, displayRef));
    }
  }, [displayRef, editingJudges]);

  const handleEditJudges = () => {
    setSelectedJudges(withoutPoolRef(currentJudges, displayRef));
    setEditingJudges(true);
  };

  const handleSaveJudges = async () => {
    setSaving(true);
    try {
      await assignPoolJudges(eventId, disciplineId, pool.name, withoutPoolRef(selectedJudges, displayRef));
      setEditingJudges(false);
    } catch (err) { console.error(err); alert("Failed to assign judges"); }
    setSaving(false);
  };

  const handleAssignRef = async (refName) => {
    if (refName && pool.fighters.includes(refName)) {
      alert(`${refName} is a fighter in ${pool.name} and cannot ref their own pool.`);
      return;
    }
    const otherPools = allPools.filter(p => p.name !== pool.name && p.ref === refName).map(p => p.name);
    if (refName && otherPools.length > 0) {
      if (!window.confirm(`${refName} is already reffing ${otherPools.join(", ")}. Make sure these pools aren't running at the same time.\n\nContinue?`)) return;
    }
    const previousRef = displayRef;
    setDisplayRef(refName);
    setSelectedJudges(current => withoutPoolRef(current, refName));
    try { await assignPoolRef(eventId, disciplineId, pool.name, refName); }
    catch (err) { setDisplayRef(previousRef); console.error(err); alert("Failed to assign ref"); }
  };

  const handleAssignRing = async (location) => {
    try { await assignPoolRing(eventId, disciplineId, pool.name, location); }
    catch (err) { console.error(err); alert("Failed to assign ring"); }
  };

  const handleUnlockRef = async () => {
    if (!window.confirm(`Unlock the ref seat for ${pool.name}? ${pool.ref} (or someone else) will be able to select "Ref" again from the pool code screen.`)) return;
    try { await releasePoolSeat(eventId, disciplineId, pool.name, "ref"); announceStatus(`Referee seat released for ${pool.name}`); }
    catch (err) { console.error(err); alert("Failed to unlock ref seat"); }
  };

  const handleUnlockJudge = async (name) => {
    if (!window.confirm(`Unlock ${name}'s seat for ${pool.name}? They'll be able to select their name again from the pool code screen.`)) return;
    try { await releasePoolSeat(eventId, disciplineId, pool.name, "judge", name); announceStatus(`${name}'s judge seat released for ${pool.name}`); }
    catch (err) { console.error(err); alert("Failed to unlock judge seat"); }
  };

  // Filter officials: exclude fighters in this pool and the pool's ref
  const availableJudges = officials.filter(o => !pool.fighters.includes(o) && o !== displayRef);
  // Only referees can be assigned as a bout's ref — plain judges can't. Keep the
  // currently-assigned ref selectable even if their role changed since, so the
  // dropdown doesn't silently disagree with what's actually saved.
  const availableRefs = officials.filter(o => (officialRoles[o] || "judge") === "referee");
  const refOptions = [...new Set(displayRef ? [...availableRefs, displayRef] : availableRefs)];
  const allDone = poolBouts.length > 0 && completed === poolBouts.length;

  return (
    <div>
      {/* Pool header */}
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-3 mb-2">
        <button onClick={onToggle} aria-expanded={isOpen}
          className="w-full flex items-center justify-between gap-2 bg-transparent border-none cursor-pointer p-0 text-left">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-display text-base font-extrabold text-white tracking-wide truncate">{pool.name}</span>
            {/* Only while collapsed — once the card is open the RING select below states
                it, and showing it in both places just repeats the same fact. */}
            {pool.location && !isOpen && (
              <span className="shrink-0 px-2 py-0.5 rounded-full bg-dark-800 border border-dark-500 text-[10px] text-gray-400">📍 {pool.location}</span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {poolBouts.length > 0 && (
              <span className="font-display text-[11px] font-bold px-2 py-0.5 rounded-full border"
                style={allDone
                  ? { color: "#81C784", borderColor: "#4CAF5066", background: "#4CAF5014" }
                  : { color: "#bbb", borderColor: "#444" }}>
                {completed}/{poolBouts.length}
              </span>
            )}
            <span aria-hidden="true" className="text-gray-600 text-xs">{isOpen ? "▲" : "▼"}</span>
          </div>
        </button>

        {poolBouts.length > 0 && (
          <div className="w-full h-1 rounded-full overflow-hidden mt-2" style={{ background: "#242424" }}>
            <div className="h-full rounded-full transition-all"
              style={{ width: `${(completed / poolBouts.length) * 100}%`, background: allDone ? "#4CAF50" : "#1E88E5" }} />
          </div>
        )}

        <PoolSection label={`FIGHTERS · ${pool.fighters.length}`}>
          <div className="flex flex-wrap gap-1.5">
            {pool.fighters.map(f => (
              <span key={f} className="px-2 py-1 bg-dark-800 border border-dark-500 rounded-lg text-[11px] text-gray-300">{f}</span>
            ))}
          </div>
        </PoolSection>

        {isOpen && (
        <>
        <PoolSection label="POOL ADMIN">
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <label className="text-[10px] font-bold text-gray-600 tracking-wider block mb-1">REF</label>
            <div className="flex items-center gap-1">
              <select aria-label={`Referee for ${pool.name}`} value={displayRef} onChange={(e) => handleAssignRef(e.target.value)}
                className="w-full p-1.5 bg-dark-800 border border-dark-500 rounded-lg text-white text-xs outline-none focus:border-gray-400 cursor-pointer">
                <option value="">— None —</option>
                {[...refOptions].sort().map(name => {
                  const isInPool = pool.fighters.includes(name);
                  return (
                    <option key={name} value={name} disabled={isInPool}>
                      {name}{isInPool ? " (in pool)" : ""}
                    </option>
                  );
                })}
              </select>
              {pool.refClaimed && (
                <button onClick={handleUnlockRef} aria-label={`Unlock referee seat for ${pool.name}`}
                  className="min-w-6 min-h-6 text-gray-600 hover:text-yellow-500 bg-transparent border-none cursor-pointer text-xs"
                  title="Unlock ref seat (e.g. they lost their device)">🔓</button>
              )}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-600 tracking-wider block mb-1">RING</label>
            <select aria-label={`Ring for ${pool.name}`} value={pool.location || ""} onChange={(e) => handleAssignRing(e.target.value)} disabled={!numRings}
              className="w-full p-1.5 bg-dark-800 border border-dark-500 rounded-lg text-white text-xs outline-none focus:border-gray-400 cursor-pointer disabled:opacity-40">
              <option value="">{numRings ? "— None —" : "Set in Settings"}</option>
              {Array.from({ length: numRings || 0 }, (_, i) => `Ring ${i + 1}`).map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>

        {!editingJudges && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-bold text-gray-600 tracking-wider">JUDGES</label>
              <button onClick={handleEditJudges}
                className="text-xs bg-transparent border-none cursor-pointer hover:text-gray-300"
              style={{ color: currentJudges.length > 0 ? "#bbb" : "#FFB74D" }}>
                {currentJudges.length > 0 ? "Edit" : "+ Assign"}
              </button>
            </div>
            {currentJudges.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {currentJudges.map(name => {
                  const isClaimed = claimedJudges.includes(name);
                  return (
                    <div key={name} className="flex items-center gap-1 pl-2 pr-1.5 py-1 bg-dark-800 border rounded-lg text-[11px]"
                      style={{ color: isClaimed ? "#81C784" : "#ccc", borderColor: isClaimed ? "#4CAF5055" : "#444" }}>
                      <span>{name}</span>
                      {isClaimed && (
                        <button onClick={() => handleUnlockJudge(name)} aria-label={`Unlock judge seat for ${name}`}
                          className="min-w-6 min-h-6 text-gray-600 hover:text-yellow-500 bg-transparent border-none cursor-pointer text-xs"
                          title="Unlock seat (e.g. they lost their device)">🔓</button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-[11px] text-gray-600 italic">None assigned yet</div>
            )}
          </div>
        )}

        {/* Judge editing */}
        {editingJudges && (
          <div>
            <div className="text-xs font-bold text-gray-500 tracking-wider mb-2">ASSIGN JUDGES FOR ALL BOUTS IN {pool.name.toUpperCase()}</div>
            {displayRef && currentJudges.includes(displayRef) && (
              <p className="text-yellow-500 text-xs mb-2">
                {displayRef} is the pool referee and will be removed from the judge list when you save.
              </p>
            )}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {availableJudges.sort().map(name => {
                const isSel = selectedJudges.includes(name);
                return (
                  <button key={name} aria-pressed={isSel} onClick={() => isSel ? setSelectedJudges(selectedJudges.filter(j => j !== name)) : setSelectedJudges([...selectedJudges, name])}
                    className="px-3 py-1.5 rounded-lg text-sm font-semibold border cursor-pointer transition-all"
                    style={{ background: isSel ? "#1B5E20" : "#111", borderColor: isSel ? "#81C784" : "#666", color: isSel ? "#fff" : "#ddd" }}>
                    {name} {isSel && "✓"}
                  </button>
                );
              })}
              {availableJudges.length === 0 && (
                <p className="text-gray-600 text-xs">No officials available. Add judges & refs from the event dashboard's JUDGES tab.</p>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditingJudges(false)}
                className="flex-1 p-2 bg-dark-800 border border-dark-500 rounded-lg text-gray-500 text-sm font-bold cursor-pointer">Cancel</button>
              <button onClick={handleSaveJudges} disabled={saving}
                className="flex-1 p-2 bg-green-action-light text-white rounded-lg text-sm font-display font-bold border-none cursor-pointer disabled:opacity-40">
                {saving ? "Saving..." : "Apply to All Bouts"}
              </button>
            </div>
          </div>
        )}
        </PoolSection>
        </>
        )}
      </div>

      {/* Join codes get their own card rather than a band inside the pool card: it's
          the one block an admin holds up to a room, and the centered code + white QR
          square never sat comfortably inside otherwise left-aligned content. */}
      {isOpen && pool.code && (
        <div className="bg-dark-700 border border-dark-500 rounded-xl p-3 mb-2">
          <div className="flex items-center justify-between mb-2.5 min-h-5">
            <PoolSectionLabel>ACCESS</PoolSectionLabel>
          </div>
          {/* Each code in its own box so the two read as a matched pair rather than a
              stack of headings and numbers. One column when there's no spectator code. */}
          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${pool.spectateCode ? 2 : 1}, minmax(0, 1fr))` }}>
            <CodeBox label="JUDGE CODE" code={pool.code} accent="#fff" />
            {pool.spectateCode && <CodeBox label="SPECTATOR CODE" code={pool.spectateCode} accent="#bbb" />}
          </div>
          <CodeQR
            path={`/pool/${eventId}/${disciplineId}/${encodeURIComponent(pool.name)}?code=${encodeURIComponent(pool.code)}`}
            label="Show official QR"
            caption="Refs scan this to join the pool"
          />
        </div>
      )}

      {/* Bout cards */}
      {isOpen && (poolBouts.length > 0
        ? (
          <>
            <div className="px-1 mb-1.5 mt-3"><PoolSectionLabel>BOUTS</PoolSectionLabel></div>
            {poolBouts.map(b => <BoutCard key={b.id} bout={b} eventId={eventId} disciplineId={disciplineId} navigate={navigate} />)}
          </>
        )
        : <p className="text-gray-600 text-xs px-1">No bouts generated yet.</p>)}
    </div>
  );
}

function BoutCard({ bout, eventId, disciplineId, navigate }) {
  const statusStyle = {
    waiting: { color: "#bbb", label: "Waiting" }, scoring: { color: "#64B5F6", label: "Scoring" },
    submitted: { color: "#FFB300", label: "Review" }, confirmed: { color: "#4CAF50", label: "✓ Done" },
  }[bout.status] || { color: "#bbb", label: bout.status };
  const judgesDone = Object.values(bout.judgeSubmissions || {}).filter(s => s.complete).length;
  const judges = bout.judges || [];

  return (
    <button onClick={() => navigate(`/event/${eventId}/discipline/${disciplineId}/bout/${bout.id}`)}
      className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-3 mb-2 cursor-pointer hover:border-gray-600 transition-colors">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          {bout.poolName && Number.isInteger(bout.order) && (
            <span className="font-display text-xs font-bold text-gray-600">#{bout.order + 1}</span>
          )}
          <span className="font-display text-sm font-bold" style={{ color: "#64B5F6" }}>{bout.fighterBlue}</span>
          <span className="text-gray-600 text-xs">vs</span>
          <span className="font-display text-sm font-bold" style={{ color: "#EF9A9A" }}>{bout.fighterRed}</span>
        </div>
        <span className="font-display text-xs font-bold" style={{ color: statusStyle.color }}>{statusStyle.label}</span>
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-600">
        {judges.length > 0 ? <span>{judgesDone}/{judges.length} judged</span> : <span className="text-yellow-700">No judges</span>}
        {bout.confirmedResult && (
          <span className="ml-auto flex items-center gap-1">
            <span className="font-display font-bold" style={{ color: "#1E88E5" }}>{bout.confirmedResult.scoreB}</span>
            <span className="text-gray-700">–</span>
            <span className="font-display font-bold" style={{ color: "#E53935" }}>{bout.confirmedResult.scoreA}</span>
            {bout.confirmedResult.winner && bout.confirmedResult.winner !== "Draw" && (
              <span className="font-display font-bold text-[10px] ml-1" style={{ color: "#4CAF50" }}>Winner: {bout.confirmedResult.winner}</span>
            )}
          </span>
        )}
      </div>
      {judges.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 mt-1.5">
          <span className="text-[10px] text-gray-600 mr-0.5">Judges:</span>
          {judges.map(j => (
            <span key={j} className="px-1.5 py-0.5 bg-dark-800 border border-dark-500 rounded text-[10px] text-gray-500">{j}</span>
          ))}
        </div>
      )}
    </button>
  );
}
